import { useContext, type PointerEventHandler } from 'react'
import { type BranchOption, type ResolvedBranchNode } from '@/lib/branchTypes'
import { ChoosePathDesignContext, SECONDARY_PLAN_CARDS } from '../choosePathDesigns'
import { splitPlanLabel } from '../splitPlanLabel'

type BranchOptionSecondaryButtonProps = {
  option: BranchOption
  targetNode: ResolvedBranchNode | null
  inverted: boolean
  onClick: () => void
  onPointerEnter?: PointerEventHandler<HTMLElement>
  onPointerLeave?: PointerEventHandler<HTMLElement>
  disabled?: boolean
  greyed?: boolean
}

// Chrome for non-recommended plan picks: inline "PLAN X" label + monospace subtitle row.
// `greyed` styles the button as visibly inactive (used to mark a plan the
// reader has just finished reading, when the menu appears at its leaf).
export const BranchOptionSecondaryButton = ({
  option,
  targetNode,
  inverted,
  onClick,
  onPointerEnter,
  onPointerLeave,
  disabled,
  greyed,
}: BranchOptionSecondaryButtonProps) => {
  const { prefix, suffix } = splitPlanLabel(option.label)
  const design = useContext(ChoosePathDesignContext)
  // The greyed (just-finished-reading) state keeps the stock chrome on every
  // design — it exists to look inactive, not designed.
  const cardSpec = greyed ? null : SECONDARY_PLAN_CARDS[design]
  if (cardSpec?.layout === 'big-letter') {
    return (
      <button
        onClick={onClick}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        disabled={disabled}
        className={`${cardSpec.buttonClassName} ${inverted ? 'bg-black text-white border-black' : ''}`}
      >
        {prefix && <span className={cardSpec.prefixClassName}>{prefix}</span>}
        <span className={cardSpec.letterClassName}>{suffix}</span>
        {targetNode?.longSubtitle && (
          <div className={`${cardSpec.subtitleClassName} ${inverted ? 'text-white/80' : ''}`}>
            {targetNode.longSubtitle}
          </div>
        )}
      </button>
    )
  }
  return (
    <button
      onClick={onClick}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      disabled={disabled}
      className={
        // A 'stock'-layout card spec swaps the chrome classes only (e.g. the
        // dark-gray box outlines); the greyed state keeps stock chrome, and
        // the selected state uses the spec's inverted chrome when it has one
        // (same geometry, filled) rather than shifting to the stock mt-2 box.
        cardSpec?.layout === 'stock' && !inverted
          ? cardSpec.buttonClassName
          : cardSpec?.layout === 'stock' && inverted && cardSpec.invertedButtonClassName
            ? cardSpec.invertedButtonClassName
            : `w-full mt-2 rounded-md font-sans text-base font-bold tracking-wide transition-colors border flex flex-col items-center justify-center py-4 border-2 ${
              greyed
                ? 'bg-black/5 text-black/40 border-black/50 cursor-default'
                : inverted
                  ? 'bg-black text-white border-black/50 cursor-pointer'
                  : 'bg-transparent border-black/50 hover:bg-black/5 cursor-pointer'
            }`
      }
    >
      <div className="flex items-center gap-1">
        {prefix && <span className="hidden xl:inline text-sm">{prefix}</span>}
        {suffix && <span className="leading-none text-xl xl:text-sm">{suffix}</span>}
      </div>
      {targetNode?.longSubtitle && (
        <div
          className={
            // A 'stock'-layout card spec may swap the subtitle classes too
            // (e.g. dropping the max-w clamp for uniform card heights).
            cardSpec?.layout === 'stock' && cardSpec.subtitleClassName && !greyed
              ? `${cardSpec.subtitleClassName} ${inverted ? 'text-white/80' : 'text-black/90'}`
              : `font-sans hidden min-[1000px]:block min-[1067px]:max-w-[50px] xl:max-w-none text-center font-normal mt-2 text-[11px] leading-snug ${greyed ? 'text-black/40' : inverted ? 'text-white/80' : 'text-black/90'}`
          }
        >
          {targetNode.longSubtitle}
        </div>
      )}
    </button>
  )
}
